import { HttpStatusCode } from 'axios';
import copy from 'copy-to-clipboard';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { partial } from 'filesize';
import { TFunction } from 'i18next';
import { SortingRule } from 'react-table';
import {
  TaxSymbolsResponse,
  TaxSymbolsResponseTransformed,
} from '../components/pm/models';
import {
  DocType,
  FiscalYearPair,
  PaginatedRequest,
  PmPaginatedRequest,
} from './common.model';
import { CdsToast } from './components/cds-toast-message/services';
import {
  BACKEND_DATE_STRING_FORMAT,
  DEFAULT_PAGE_SIZE,
  DEFAULT_USER_LOCALE,
  DEFAULT_USER_TIMEZONE,
} from './constants/common';

dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);
dayjs.extend(duration);

export const setDefaultTimezone = (timezone: string | undefined) => {
  if (timezone) {
    dayjs?.tz?.setDefault(timezone);
  }
};

export const getCurrentFiscalStartYear = () => {
  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();

  return month < 3 ? year - 1 : year;
};

export const getCurrentFiscalYearPair = () => {
  const currentFiscalStartYear = getCurrentFiscalStartYear();
  const currentFiscalYear: FiscalYearPair = {
    from: currentFiscalStartYear,
    to: currentFiscalStartYear + 1,
  };

  return currentFiscalYear;
};

export const convertSortByToApiFormat = (
  sortBy: SortingRule<object>[],
): string =>
  sortBy.length ? `${sortBy[0].id},${sortBy[0].desc ? 'desc' : 'asc'}` : '';

export const transformFiltersToApiFormat = (req?: PaginatedRequest) => {
  if (!req) {
    return {};
  }

  const { page = 0, size = DEFAULT_PAGE_SIZE, sortBy, query } = req;
  const sort = convertSortByToApiFormat(sortBy);
  return {
    ...(!!sort && { sort }),
    ...(!!query && { query }),
    size,
    page,
  };
};

export const transformFiltersToPmApiFormat = (
  req: PmPaginatedRequest = {
    page_num: 0,
    sort_by: [],
    query: '',
  },
  size?: number,
) => {
  if (size !== undefined) {
    req.page_size = size;
  }

  const {
    page_num = 0,
    page_size = DEFAULT_PAGE_SIZE,
    sort_by = [],
    query,
    filter,
    custom_params,
  } = req;

  const sort = convertSortByToApiFormat(sort_by);

  return {
    ...(!!sort && { sort }),
    ...(!!query && { query }),
    page_size,
    page_num,
    filter,
    ...(custom_params || {}),
  };
};

export const getCurrentUserTimeISOString = () => {
  return dayjs.tz()?.format('YYYY-MM-DDTHH:mm:ssZ');
};

export const convertLocalDateToOrgDate = (date: dayjs.ConfigType) => {
  return dayjs.tz(dayjs(date).format('YYYY-MM-DDTHH:mm:ss')).toDate();
};

export const formatUtcDateToLocalDate = (date: dayjs.ConfigType) =>
  dayjs(dayjs(date).tz().format('YYYY-MM-DDTHH:mm:ss')).toDate();

export const formatDateShort = (date: dayjs.ConfigType) =>
  dayjs(date).tz().format('ll');

export const formatDateShortWithoutTimeZone = (date: dayjs.ConfigType) =>
  dayjs(date).format('ll');

export const formatDateAndMonthOnly = (date?: dayjs.ConfigType) =>
  !date ? null : dayjs(date).tz().format('MMMM D');
export const formatDateLong = (date: dayjs.ConfigType) =>
  dayjs(date).tz().format('LL');

export const formatDateLongOrDefaultText = (date: dayjs.ConfigType) =>
  date ? formatDateLong(date) : 'TBD';
export const formatDateTimeShort = (date: dayjs.ConfigType) =>
  dayjs(date).format('lll');

export const formatDateAndTimeShort = (
  date: dayjs.ConfigType,
  timezone?: string,
) => dayjs(date).tz(timezone).format('ll hh:mm A');

export const formatDateWorkOs = (t: TFunction, date: dayjs.ConfigType) => {
  if (!date) return '';

  const today = dayjs.tz();
  const dDate = dayjs(date).tz();

  const isToday = dDate.isSame(today, 'day');
  const isTomorrow = dDate.isSame(today.add(1, 'day'), 'day');
  const isYesterday = dDate.isSame(today.add(-1, 'day'), 'day');

  if (isToday) return t('today');
  if (isTomorrow) return t('tomorrow');
  if (isYesterday) return t('yesterday');

  const daysDiff = dDate.diff(today, 'day');

  if (today.year() !== dDate.year()) return formatDateShort(date);

  if (daysDiff < 0 && daysDiff > -6)
    return dayjs.duration(daysDiff, 'day').humanize(true);

  if (daysDiff > 0 && daysDiff < 6) return dDate.format('dddd');

  if (daysDiff < 364 || daysDiff > -364) return dDate.format('MMM D');

  return formatDateShort(date);
};

export const downloadBlob = (function () {
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style.display = 'none';
  return function (data: any, fileName: string) {
    const blob = new Blob([data], { type: 'octet/stream' });
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };
})();

export const getDocTypeFromFileName = (fileName: string): DocType => {
  const extension = (fileName || '').split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return DocType.Pdf;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return DocType.Image;
    default:
      return DocType.None;
  }
};

export const getChartInterval = (length: number): number => {
  if (length > 25) {
    return 5;
  } else if (length > 15) {
    return 4;
  } else if (length > 10) {
    return 3;
  } else if (length > 5) {
    return 2;
  } else {
    return 0;
  }
};

export const getTimeDifference = (date: Date): number => {
  const date1 = dayjs(date);
  const date2 = dayjs();
  const hours = date2.diff(date1, 'minutes');
  return hours;
};

export const getTimeDifferenceInHours = (date: Date): number => {
  const date1 = dayjs(date);
  const date2 = dayjs();
  const hours = date2.diff(date1, 'hour');
  return hours;
};

export const getDaysDifference = (date: Date): number => {
  const date1 = dayjs(date);
  const date2 = dayjs();
  const days = date2.diff(date1, 'day');
  return days;
};

export const getTextFromHtml = (html: string): string => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const toNumber = (
  value: string | undefined | number,
): number | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return Number(value);
};

export const formatDateByTimezone = (
  date: dayjs.ConfigType,
  timezone: string | undefined,
) => dayjs(date).tz(timezone).format('ll');

export const formatDateLocal = (date: dayjs.ConfigType) =>
  dayjs(date).format('ll');

export const formatDateTime = (
  date: dayjs.ConfigType,
  timezone: string,
  locale: string,
) => {
  const timeZoneAbbr = new Intl.DateTimeFormat(locale || DEFAULT_USER_LOCALE, {
    timeZone: timezone || DEFAULT_USER_TIMEZONE,
    timeZoneName: 'short',
  })
    .format(Date.now())
    .split(' ')[1];

  return (
    dayjs(date)
      .tz(timezone || DEFAULT_USER_TIMEZONE)
      .format('lll') +
    ' ' +
    timeZoneAbbr
  );
};

export const dataUrlToBlob = (dataUrl: string): Blob => {
  const byteString = atob(dataUrl.split(',')[1]);
  const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uintArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uintArray[i] = byteString.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mimeString });
};

export const fileToBase64 = (file: File) =>
  new Promise<string | null>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

export const base64ToFile = (base64: string, filename: string) => {
  const byteString = atob(base64.split(',')[1]);
  const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const dw = new DataView(ab);
  for (let i = 0; i < byteString.length; i++) {
    dw.setUint8(i, byteString.charCodeAt(i));
  }
  return new File([ab], filename, { type: mimeString });
};

export const blobToBase64 = (blob: Blob) =>
  new Promise<string | null>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

export const base64ToBlob = (base64: string) => {
  const byteString = atob(base64.split(',')[1]);
  const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const dw = new DataView(ab);
  for (let i = 0; i < byteString.length; i++) {
    dw.setUint8(i, byteString.charCodeAt(i));
  }
  return new Blob([ab], { type: mimeString });
};

export const imageUrlToDataURI = async (url: string): Promise<string> => {
  // Fetch the image
  const response = await fetch(url);

  // Check if the fetch was successful
  if (!response.ok) {
    throw new Error(`Failed to fetch image. Status: ${response.status}`);
  }

  // Get image as blob
  const blob = await response.blob();

  // Convert blob to dataURI
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        resolve(reader.result as string);
      } else {
        reject(new Error('Failed to read the blob as data URI'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read the blob'));
    };

    reader.readAsDataURL(blob);
  });
};

export const copyToClipboard = (link: string, successMessage?: string) => {
  if (copy(link)) {
    if (successMessage) {
      CdsToast.success(successMessage, {
        toastId: 'copy-to-clipboard-toast',
      });
    }
  }
};

export const formatFileSize = (size: number) => {
  const fileSizeObject = partial({ bits: false, output: 'object' });
  const sizeData = fileSizeObject(size) as {
    value: any;
    symbol: any;
    exponent: number;
    unit: string;
  };

  if (sizeData.symbol === 'B') {
    return `${Math.round(sizeData.value / 10) / 100.0} KB`;
  } else if (sizeData.symbol === 'KB') {
    return `${Math.round(sizeData.value)} ${sizeData.symbol}`;
  }

  return `${sizeData.value} ${sizeData.symbol}`.toLocaleUpperCase();
};

export const formatRunningTimer = (elapsedTime: number) => {
  const diffInSec = Math.floor(elapsedTime);

  const hours = Math.floor(diffInSec / 3600)
    .toString()
    .padStart(2, '0');

  const minutes = Math.floor((diffInSec % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (diffInSec % 60).toString().padStart(2, '0');

  const text = `${hours}:${minutes}:${seconds}`;

  return text;
};

export const formatSecondsToHrs = (
  seconds: number,
  optionalHour?: boolean,
): string => {
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  if (optionalHour) {
    return `${hours !== '00' ? `${hours}h ` : ''}${minutes}m`;
  }
  return `${hours}h ${minutes}m`;
};

export const formatTimeStampToDateLabel = (timestamp: string, t: TFunction) => {
  const isToday = dayjs(timestamp).tz().isToday();
  if (isToday) return t('today');
  const isYesterday = dayjs(timestamp).tz().isYesterday();
  if (isYesterday) return t('yesterday');
  return formatDateShort(timestamp);
};

export const addTimeToTimestamp = (timeStr: string, startTimestamp: string) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const startDate = new Date(startTimestamp);
  startDate.setUTCHours(hours);
  startDate.setUTCMinutes(minutes);

  return new Date(
    new Date(startDate).getTime() +
      new Date(startDate).getTimezoneOffset() * 60 * 1000,
  ).toISOString();
};

export const isSameOrBeforeTheEndDate = (
  startDate: Date | string,
  endDate: Date | string,
  unit: dayjs.OpUnitType,
) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return dayjs(start).isSameOrBefore(end, unit);
};

export const isSameOrAfterTheStartDate = (
  startDate: Date | string,
  endDate: Date | string,
  unit: dayjs.OpUnitType,
) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return dayjs(end).isSameOrAfter(start, unit);
};

export const parsedDateWithFormat = (
  dateString: string,
  format: dayjs.OptionType,
) => dayjs(dayjs(dateString).tz(), format, true);

export const isValidUrl = (url: string) => {
  const urlPattern =
    /^(https?:\/\/)?([\w-]+(\.[\w-]+)+\/?|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(:\d+)?(\/\S*)?$/;
  return urlPattern.test(url);
};

export const isValidStringNumber = (
  value: string | number | undefined | null,
) => value === 0 || (!!value && !isNaN(Number(value)));

export const defaultIfNotValidNumber = (
  value: number | string | undefined | null,
  defaultValue?: number | undefined | null,
) => (isValidStringNumber(value) ? Number(value) : defaultValue);

export const convertStringToNumberIfValid = (
  value: string | number | undefined | null,
  defaultValue?: number | undefined,
) => (isValidStringNumber(value) ? Number(value) : defaultValue);

export const roundUpTo = (
  num: string | number | undefined | null,
  decimalPlaces: number = 2,
): number =>
  Math.round(
    ((convertStringToNumberIfValid(num) || 0) + Number.EPSILON) *
      Math.pow(10, decimalPlaces),
  ) / Math.pow(10, decimalPlaces);

export const roundUpAmount = (
  num: string | number | undefined | null,
): number => roundUpTo(num, 2);

export const defaultIfEmptyString = (
  value: string | undefined,
  defaultValue?: string | undefined,
) => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : defaultValue;
};

type TimezoneAbbreviation = {
  [key: string]: string;
};

export const timeZoneCorrections: TimezoneAbbreviation = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Bombay': 'Asia/Mumbai',
  'Australia/Sydney': 'Australia/Sydney',
  'Europe/Belfast': 'Europe/London',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
};

export const convertTimezoneToAbbreviation = (timezone: string) => {
  const timezoneAbbreviations: TimezoneAbbreviation = {
    'US/Pacific': 'PST/PDT',
    'US/Eastern': 'EST/EDT',
    'US/Central': 'CST/CDT',
    'US/Mountain': 'MST/MDT',
    'US/Arizona': 'MST',
    'US/Alaska': 'AKST/AKDT',
    'US/Hawaii': 'HST',
    'America/Los_Angeles': 'PST/PDT',
    'America/New_York': 'EST/EDT',
    'America/Chicago': 'CST/CDT',
    'America/Denver': 'MST/MDT',
    'America/Phoenix': 'MST',
    'America/Anchorage': 'AKST/AKDT',
    'Pacific/Honolulu': 'HST',
    'Canada/Eastern': 'EST/EDT',

    'Europe/London': 'GMT/BST',
    'Europe/Dublin': 'IST',
    'Europe/Paris': 'CET/CEST',
    'Europe/Berlin': 'CET/CEST',
    'Europe/Moscow': 'MSK',
    'Europe/Athens': 'EET/EEST',

    'Asia/Kolkata': 'IST',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Dubai': 'GST',
    'Asia/Singapore': 'SGT',
    'Asia/Seoul': 'KST',
    'Asia/Jerusalem': 'IST',
    'Asia/Taipei': 'CST',
    'Asia/Bangkok': 'ICT',

    'Australia/Sydney': 'AEST/AEDT',
    'Australia/Perth': 'AWST',
    'Australia/Adelaide': 'ACST/ACDT',
    'Australia/Brisbane': 'AEST',
    'Pacific/Auckland': 'NZST/NZDT',
    'Pacific/Fiji': 'FJT',

    'Africa/Johannesburg': 'SAST',
    'Africa/Cairo': 'EET',
    'Africa/Lagos': 'WAT',
    'Africa/Nairobi': 'EAT',

    'America/Sao_Paulo': 'BRT/BRST',
    'America/Buenos_Aires': 'ART',
    'America/Santiago': 'CLT/CLST',
  };
  return timezoneAbbreviations[timezone] || timezone;
};

export enum BrowserName {
  Edge,
  InternetExplorer,
  Firefox,
  Opera,
  UcBrowser,
  SamsungBrowser,
  Chrome,
  Safari,
  Unknown,
}

const testUserAgent = (regexp: RegExp): boolean =>
  regexp.test(window.navigator.userAgent);

function detectBrowser(): BrowserName {
  switch (true) {
    case testUserAgent(/edg/i):
      return BrowserName.Edge;
    case testUserAgent(/trident/i):
      return BrowserName.InternetExplorer;
    case testUserAgent(/firefox|fxios/i):
      return BrowserName.Firefox;
    case testUserAgent(/opr\//i):
      return BrowserName.Opera;
    case testUserAgent(/ucbrowser/i):
      return BrowserName.UcBrowser;
    case testUserAgent(/samsungbrowser/i):
      return BrowserName.SamsungBrowser;
    case testUserAgent(/chrome|chromium|crios/i):
      return BrowserName.Chrome;
    case testUserAgent(/safari/i):
      return BrowserName.Safari;
    default:
      return BrowserName.Unknown;
  }
}

export const CurrentBrowser: BrowserName = detectBrowser();

export const isSafari = CurrentBrowser === BrowserName.Safari;

export const getRandomArbitrary = (
  min: number | string,
  max: number | string,
) => Math.random() * (Number(max) - Number(min)) + Number(min);

export const isPlateRichTextEditorJsonEmpty = (
  jsonString: string | undefined,
) => {
  if (!jsonString) return true;

  try {
    const json = JSON.parse(jsonString);

    return (
      json.length === 1 &&
      json[0]?.children?.length === 1 &&
      json?.[0]?.children?.[0]?.text?.trim() === ''
    );
  } catch (e) {
    console.error(e);
    return true;
  }
};

export const getDateDifferenceInDaysFromToday = (to: Dayjs | string) =>
  Math.ceil(dayjs(to).diff(dayjs(), 'day', true));

export const adjustDateToLocalAsUTC = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset);
};

export const catchAndSkipToastOnStatusCode = (statusCode: HttpStatusCode) => {
  const fn: (reason: any) => PromiseLike<never> = (err) => {
    if (err?.status_code === statusCode) {
      return Promise.reject({
        ...err,
        skipToast: true,
      });
    }

    return Promise.reject(err);
  };

  return fn;
};

export const goBackOrDo = (doFn: () => void) =>
  window.history.length > 2 ? window.history.back() : doFn();

export const getRandomInt = (min: number, max: number) => {
  const minCeiled = Math.ceil(min);
  const maxFloored = Math.floor(max);
  return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled); // The maximum is exclusive and the minimum is inclusive
};

/**
 * Processes TaxSymbolsResponse to return unique enums with their details.
 * @param data Array of TaxSymbolsResponse
 * @returns Array of objects with unique enums and aggregated details
 */
export const processTaxSymbolsResponse = (
  data: TaxSymbolsResponse[],
): Array<TaxSymbolsResponseTransformed> => {
  const uniqueEnumsMap = new Map<
    string,
    {
      enum: string;
      example: string;
      description: string;
      countries: Set<string>;
    }
  >();

  data.forEach((item) => {
    const existing = uniqueEnumsMap.get(item.enum);

    if (existing) {
      existing.countries.add(item.country);
    } else {
      uniqueEnumsMap.set(item.enum, {
        enum: item.enum,
        example: item.example,
        description: item.description,
        countries: new Set([item.country]),
      });
    }
  });

  return Array.from(uniqueEnumsMap.values()).map((entry) => ({
    ...entry,
    countries: Array.from(entry.countries),
  }));
};

export const randomElementColorGenerator = (value: string) => {
  const BgColorAndTextColorMap = [
    'bg-red-100 text-red-900',
    'bg-amber-100 text-amber-900',
    'bg-lime-100 text-lime-900',
    'bg-green-100 text-green-900',
    'bg-teal-100 text-teal-900',
    'bg-sky-100 text-sky-900',
    'bg-blue-100 text-blue-900',
    'bg-indigo-100 text-indigo-900',
    'bg-violet-100 text-violet-900',
    'bg-pink-100 text-pink-900',
  ];

  if (value) {
    return BgColorAndTextColorMap[0];
  }

  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  const index = Math.abs(h % 10);
  return BgColorAndTextColorMap[index];
};

export const isPastDate = (date: dayjs.ConfigType) =>
  dayjs(date).isBefore(dayjs(), 'day');

export const getMaxDate = (
  date1: dayjs.ConfigType,
  date2: dayjs.ConfigType = new Date(),
) => {
  const d1 = dayjs(date1);
  const d2 = dayjs(date2);

  return d1.isBefore(d2, 'day') ? d2 : d1;
};

export const getDateInBackendStringFormat = (date: dayjs.ConfigType) =>
  dayjs(date).format(BACKEND_DATE_STRING_FORMAT);
